import React from "react";
import avatarPlaceholder from "./../../../../../assets/images/avatar-placeholder.png";
import AiGlobe from "./../../../../../assets/images/ai-globe.svg";
import { motion } from "framer-motion";
import useTitanApp from "../../../../../hooks/useTitanApp";
import { FiArrowRight } from "react-icons/fi";
import {
  setChatBotReferralLetter,
  setChatQuestions,
  setIsChatReferralLetter,
  setAddPatientAIChatMessage,
  setAiChatSuccessMessages,
} from "../../../../../dispatchers/patientDispatchers";
import SuccessActionMessage from "./SuccessActionMessage";

const extractMessageAndActions = (rawMessage) => {
  let cleanedMessage = rawMessage
    .replace(/```/g, "")
    .replace(/\bjson\b/gi, "")
    .trim();
  let messageText = cleanedMessage;
  let actions = [];
  const fullJsonIndex = cleanedMessage.indexOf('{"Actions":');
  const partialJsonIndex = cleanedMessage.indexOf('"Actions":');
  if (fullJsonIndex !== -1 || partialJsonIndex !== -1) {
    const jsonStart = fullJsonIndex !== -1 ? fullJsonIndex : partialJsonIndex;
    messageText = cleanedMessage.substring(0, jsonStart).trim();
    let jsonStr = cleanedMessage.substring(jsonStart).trim();
    if (partialJsonIndex !== -1 && fullJsonIndex === -1) {
      jsonStr = `{${jsonStr}`;
    }

    try {
      const parsed = JSON.parse(jsonStr);
      actions = parsed.Actions || [];
    } catch (err) {
      console.warn("Failed to parse Actions JSON:", err, jsonStr);
    }
  }
  return { messageText, actions };
};

function ChatMessage({
  chat,
  chatIdx,
  isLastAIMessage,
  isTyping,
  showLoader,
  darkMode,
  getActions,
  onActionClick,
  patientAIChatMessages,
  isLatest,
}) {
  const { messageText, actions } = React.useMemo(
    () => extractMessageAndActions(chat.message || ""),
    [chat.message],
  );

  const getReferralLetterData = (action, messageText) => {
    return {
      title: action?.Title || "Referral Letter",
      subject: action?.Title || "Referral Letter",
      content: messageText,
    };
  };

  if (chat.sender === "success") {
    return (
      <motion.div className="chat-box success-message">
        <SuccessActionMessage
          actionId={chat.actionId}
          details={chat.details}
          isLatest={isLatest}
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      className={`chat-box ${
        chat.sender === "ai" ? "answer-from-ai" : "question-from-user"
      }`}
    >
      {showLoader ? (
        <div className="answers-loader">
          <div className="circle circle-one"></div>
          <div className="circle circle-two"></div>
          <div className="circle circle-three"></div>
        </div>
      ) : (
        <div className="chat-content-wrapper">
          <div className="text">
            <p>
              {chat.sender === "ai"
                ? messageText.split("{")[0].trim()
                : (chat.message || "").split("{")[0].trim()}
              {isTyping && isLastAIMessage && (
                <span className="cursor-chat">|</span>
              )}
            </p>
          </div>
          {actions.length > 0 && (
            <div className="action-box-wrapper">
              {actions.map((action, index) => (
                <div
                  key={index}
                  className="action-box"
                  onClick={() => {
                    setIsChatReferralLetter(true);
                    const referralLetterData = getReferralLetterData(
                      action,
                      messageText,
                    );
                    setChatBotReferralLetter(referralLetterData);
                    getActions(
                      action?.ActionNumber || action?.actionNumber || "",
                    );
                    if (onActionClick && patientAIChatMessages) {
                      for (let i = chatIdx - 1; i >= 0; i--) {
                        const prev = patientAIChatMessages[i];
                        if (prev?.sender === "user") {
                          onActionClick(prev.message);
                          break;
                        }
                      }
                    }
                  }}
                >
                  <div className="left">
                    <p className="action-name">{action.ActionName}</p>
                    <p className="action-description">{action.Description}</p>
                  </div>
                  <FiArrowRight
                    className="arrow"
                    color={darkMode ? "#fff" : "#0a0a0a"}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="avatar">
        {chat.sender === "ai" ? (
          <AiGlobe />
        ) : (
          <img src={avatarPlaceholder} alt="avatar" />
        )}
      </div>
    </motion.div>
  );
}

export default function PatientAIChatbot({ isTyping, chatBoxRef, getActions }) {
  const { appState, patientState } = useTitanApp();
  const { darkMode } = appState || {};
  const {
    patientChatAnswersLoader,
    patientAIChatMessages = [],
    signatureDetails,
    prescriptionCompleted,
    paymentCompleted,
    referralCompleted,
    bookingCompleted,
    vitalsCompleted,
    vaccineCompleted,
    signatureCompleted,
    vitalsDetails,
    vaccineDetails,
    bookingDetails,
    referralLetter,
    prescriptionDetails,
    letterDetails,
  } = patientState || {};
  const scrollRef = React.useRef(null);
  const nonSuccessMessages = React.useMemo(
    () => (patientAIChatMessages || []).filter((m) => m?.sender !== "success"),
    [patientAIChatMessages],
  );

  React.useEffect(() => {
    const scrollToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollIntoView({ behavior: "smooth" });
      }
    };
    const raf = requestAnimationFrame(scrollToBottom);
    return () => cancelAnimationFrame(raf);
  }, [patientAIChatMessages]);

  React.useEffect(() => {
    return () => {
      setAiChatSuccessMessages();
    };
  }, []);

  const extractPrescriptionIdFromMsg = (msg) => {
    if (!msg) return null;
    let details = msg.details;

    if (typeof details === "string") {
      try {
        details = JSON.parse(details);
      } catch {}
    }

    if (details?.payload && typeof details.payload === "string") {
      try {
        const parsedPayload = JSON.parse(details.payload);
        if (parsedPayload?.prescriptionDetails?.prescriptionId) {
          return parsedPayload.prescriptionDetails.prescriptionId;
        }
        if (parsedPayload?.prescriptionId) return parsedPayload.prescriptionId;
      } catch {}
    }

    if (details?.prescriptionDetails?.prescriptionId) {
      return details.prescriptionDetails.prescriptionId;
    }
    if (details?.prescriptionId) return details.prescriptionId;

    return null;
  };

  const lastPrescriptionIndexMap = {};
  (patientAIChatMessages || []).forEach((msg, i) => {
    if (msg?.sender === "success" && msg?.actionId === 1) {
      const pid = extractPrescriptionIdFromMsg(msg);
      if (pid) {
        lastPrescriptionIndexMap[pid] = i;
      }
    }
  });

  // Only keep the latest success message for each actionId
  const latestSuccessMessages = {};
  patientAIChatMessages.forEach((msg, idx) => {
    if (msg.sender === "success" && msg.actionId) {
      if (msg.actionId === 5) {
        // Parse payload to get vital type
        let vitalType = null;
        let details = msg.details;
        if (details?.payload) {
          try {
            const payloadObj = JSON.parse(details.payload);
            details = payloadObj.vitalsDetails || details;
          } catch {}
        }
        const vitalKeys = [
          "heartRate",
          "weight",
          "bloodPressure",
          "temperature",
          "respiratoryRate",
          "height",
          "circumference",
          "bmi",
        ];
        for (const key of vitalKeys) {
          if (details && details[key]) {
            vitalType = key;
            break;
          }
        }
        if (vitalType) {
          latestSuccessMessages[`5-${vitalType}`] = { ...msg, idx };
        }
      } else {
        latestSuccessMessages[msg.actionId] = { ...msg, idx };
      }
    }
  });
  return (
    <div className="chat-box-wrapper" ref={chatBoxRef}>
      {nonSuccessMessages?.map((chat, idx) => {
        const isLastAIMessage =
          chat.sender === "ai" && idx === patientAIChatMessages.length - 1;

        let isLatest = false;

        if (chat.sender === "success" && chat.actionId === 1) {
          const pid = extractPrescriptionIdFromMsg(chat);
          if (pid) {
            isLatest = lastPrescriptionIndexMap[pid] === idx;
          } else {
            isLatest = true;
          }
        } else if (chat.sender === "success" && chat.actionId === 5) {
          const extractVitalKey = (msg) => {
            let d = msg?.details;
            if (!d) return null;
            if (typeof d === "string") {
              try {
                d = JSON.parse(d);
              } catch {}
            }
            if (d?.payload) {
              try {
                const parsed = JSON.parse(d.payload);
                d = parsed.vitalsDetails || d;
              } catch {}
            }
            const vitalKeys = [
              "heartRate",
              "weight",
              "bloodPressure",
              "temperature",
              "respiratoryRate",
              "height",
              "circumference",
              "bmi",
            ];
            return vitalKeys.find((k) => d && d[k]) || null;
          };

          const vitalKey = extractVitalKey(chat);
          if (vitalKey) {
            // find last index
            let lastIndexForVital = -1;
            for (let i = 0; i < patientAIChatMessages.length; i++) {
              const m = patientAIChatMessages[i];
              if (m?.sender === "success" && m?.actionId === 5) {
                const vk = extractVitalKey(m);
                if (vk === vitalKey) lastIndexForVital = i;
              }
            }
            isLatest = lastIndexForVital === idx;
          } else {
            isLatest = true;
          }
        } else if (chat.sender === "success" && chat.actionId) {
          // other success actions: keep only the last occurrence
          // find last index for this actionId
          let lastIdxForAction = -1;
          for (let i = 0; i < patientAIChatMessages.length; i++) {
            const m = patientAIChatMessages[i];
            if (m?.sender === "success" && m?.actionId === chat.actionId) {
              lastIdxForAction = i;
            }
          }
          isLatest = lastIdxForAction === idx;
        }

        return (
          <React.Fragment key={idx}>
            <ChatMessage
              chat={chat}
              chatIdx={idx}
              isLastAIMessage={isLastAIMessage}
              isTyping={isTyping}
              showLoader={patientChatAnswersLoader && isLastAIMessage}
              darkMode={darkMode}
              getActions={getActions}
              patientAIChatMessages={nonSuccessMessages}
              isLatest={isLatest}
              onActionClick={(userMessage) => {
                setChatQuestions(userMessage);
                setChatBotReferralLetter("");
              }}
            />
            <div ref={scrollRef}></div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
